import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { DetailIconComponent } from '@sharedModule/components/detail-icon/detail-icon.component';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { StatusPillPipe } from '@sharedModule/pipe/status-pill/status-pill.pipe';
import { Subject, takeUntil } from 'rxjs';
import { RecipientService } from '../../services/recipient.service';
import { RecipientListInterface } from '../../types/recipient-list';
import { CommonModule } from '@angular/common';

const GENDER_LABELS: Record<string, string> = { M: 'Male', F: 'Female' };

@Component({
  selector: 'app-recipient-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HasAccessDirective,
    DetailIconComponent,
    StatusPillPipe,
  ],
  templateUrl: './recipient-details.component.html',
  styleUrl: './recipient-details.component.scss',
})
export class RecipientDetailsComponent implements OnInit, OnDestroy {
  recipientData?: RecipientListInterface;
  isSuperAdmin!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private recipientService: RecipientService,
    private authenticationService: AuthenticationService
  ) {
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  /** Recipient name with the optional middle name collapsed out. */
  get fullName(): string {
    if (!this.recipientData) return '';
    return [
      this.recipientData.bene_first_name,
      this.recipientData.bene_middle_name,
      this.recipientData.bene_last_name,
    ]
      .filter(Boolean)
      .join(' ');
  }

  get genderLabel(): string {
    const gender = this.recipientData?.gender ?? '';
    return GENDER_LABELS[gender] || gender || '—';
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    const payload = this.authenticationService.isSuperAdmin()
      ? {
          bene_id: +id.split('-')[0],
          partner_id: +id.split('-')[1],
        }
      : { bene_id: +id };
    this.recipientService
      .getRecipientDetails(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.recipientData = x.response;
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
